import { log } from '@/src/lib/logger/business/log';
import { HttpClient, HttpClientOptions, HttpClientPlugin, InternalRequestInit } from '../types';
import * as Sentry from '@sentry/nextjs';

async function executeBeforeRequestPlugins(
  url: string,
  options: InternalRequestInit,
  plugins: Array<HttpClientPlugin>,
) {
  let result = { url, options };
  for (const plugin of plugins) {
    if (plugin.beforeRequest) {
      result = await plugin.beforeRequest(result.url, result.options);
    }
  }

  return result;
}

export function createBaseClient<Init = InternalRequestInit>(
  clientOptions: HttpClientOptions,
  responseHandler: <T>(res: Response) => Promise<T | never>,
): HttpClient<Init> {
  const plugins = clientOptions.plugins || [];
  async function get(url: string, options?: Init) {
    const result = await executeBeforeRequestPlugins(url, { method: 'GET', ...options }, plugins);
    log({
      message: [
        `GET ${url}`,
        { headers: Object.fromEntries(result.options.headers?.entries() || []), options: result.options },
      ],
    });
    return fetch(result.url, result.options);
  }

  async function post(url: string, options?: Init) {
    const result = await executeBeforeRequestPlugins(url, { method: 'POST', ...options }, plugins);
    log({
      message: [
        `POST ${url}`,
        { headers: Object.fromEntries(result.options.headers?.entries() || []), options: result.options },
      ],
    });

    Sentry.setContext('request', { url: result.url, options: result.options });

    return fetch(result.url, result.options);
  }

  async function patch(url: string, options?: Init) {
    const result = await executeBeforeRequestPlugins(url, { method: 'PATCH', ...options }, plugins);
    log({
      message: [
        `PATCH ${url}`,
        { headers: Object.fromEntries(result.options.headers?.entries() || []), options: result.options },
      ],
    });
    return fetch(result.url, result.options);
  }

  async function del(url: string, options?: Init) {
    const result = await executeBeforeRequestPlugins(url, { method: 'DELETE', ...options }, plugins);
    log({
      message: [
        `DELETE ${url}`,
        { headers: Object.fromEntries(result.options.headers?.entries() || []), options: result.options },
      ],
    });
    return fetch(result.url, result.options);
  }

  return {
    get,
    patch,
    post,
    delete: del,
    handleResponse: responseHandler,
  };
}
